import {OpenAPI, SasUploadLinkResponse, StayDetail, TourDetail, TourService} from "../../services/restserver-openapi";
import {UserVanliferService} from "../../services/restserver-openapi";

export function setTokenTourInApi(identityToken: string) {
    OpenAPI.TOKEN = identityToken;
}
export const getToursFromUser = (sessionId?: string, vanliferId?: number, tourStatus?: 'BOOKED' | 'CANCELED') =>
    TourService.getTours(sessionId, vanliferId, tourStatus);

export const getFuturePlannedToursCount = async (vanliferId?: number): Promise<number> => {
    const headers = getAuthorizedHeaders();
    const query = vanliferId ? `?vanliferId=${vanliferId}` : '';

    const response = await fetch(`${OpenAPI.BASE}/tours/future-planned/count${query}`, {
        method: 'GET',
        headers,
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || 'Unable to load future planned tours count');
    }

    return response.json();
};

export const getGeneratedTours = (fromDate: number, toDate: number, regionId: number, compoundTypes: string[], curationId: number) =>
    TourService.generateTours(regionId, fromDate, toDate, undefined, compoundTypes, curationId);

export const reserveTour = (tourId: number)=>
    TourService.reserveTour(tourId);

export const exchangeStay = (tourId: number, stayId: number, siteId: number)=>
    TourService.replaceSite( tourId, stayId, siteId);

export const changeStayDuration = (tourId: number, stayId: number, newFromDate: number, newToDate: number)=>
    TourService.changeStayDuration(tourId, stayId, newFromDate, newToDate );

export const postBookTour = (tourId: number)=>{
    return TourService.bookTour(tourId);
}

export const changeStayAdditionalInformation = (tourId: number, stayId: number, arrivalTime: string, comment: string)=>
    TourService.changeStayAdditionalInformation(tourId, stayId, arrivalTime, comment);

export type UpdateStayStatusPayload = {
    stayStatus?: StayDetail.stayStatus;
    statusChannel?: StayDetail.statusChannel;
    statusNote?: string;
}

export type CustomStayPayload = {
    stayType?: StayDetail.stayType;
    customTitle?: string;
    customDescription?: string;
    customAddressLine1: string;
    customAddressLine2?: string;
    customZipCode: string;
    customCity: string;
    customCoordinatesX: number;
    customCoordinatesY: number;
    fromDate: number;
    toDate: number;
}

const HOST_STAY_TYPE = 'HOST' as StayDetail.stayType;
const CUSTOM_START_STAY_TYPE = 'CUSTOM_START' as StayDetail.stayType;
const CUSTOM_END_STAY_TYPE = 'CUSTOM_END' as StayDetail.stayType;

function getAuthorizedHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json"
    };

    const token = sessionStorage.getItem('token');
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return headers;
}

export const updateStayStatus = async (tourId: number, stayId: number, payload: UpdateStayStatusPayload): Promise<StayDetail> => {
    const headers = getAuthorizedHeaders();

    const response = await fetch(`${OpenAPI.BASE}/tours/${tourId}/mutations/stays/${stayId}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || "Unable to update stay status");
    }

    return response.json();
}

export const addCustomStay = async (tourId: number, payload: CustomStayPayload): Promise<TourDetail> => {
    const response = await fetch(`${OpenAPI.BASE}/tours/${tourId}/mutations/custom-stays`, {
        method: "PUT",
        headers: getAuthorizedHeaders(),
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || "Unable to add custom stay");
    }

    return response.json();
};

export const updateCustomStay = async (tourId: number, stayId: number, payload: CustomStayPayload): Promise<TourDetail> => {
    const response = await fetch(`${OpenAPI.BASE}/tours/${tourId}/mutations/custom-stays/${stayId}`, {
        method: "PATCH",
        headers: getAuthorizedHeaders(),
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || "Unable to update custom stay");
    }

    return response.json();
};

function buildBoundaryCustomStayPayload(stay: StayDetail, targetDate: number): CustomStayPayload | null {
    if (
        !stay.customAddressLine1 ||
        !stay.customZipCode ||
        !stay.customCity ||
        stay.customCoordinatesX == null ||
        stay.customCoordinatesY == null
    ) {
        return null;
    }

    return {
        stayType: stay.stayType,
        customTitle: stay.customTitle,
        customDescription: stay.customDescription,
        customAddressLine1: stay.customAddressLine1,
        customAddressLine2: stay.customAddressLine2,
        customZipCode: stay.customZipCode,
        customCity: stay.customCity,
        customCoordinatesX: stay.customCoordinatesX,
        customCoordinatesY: stay.customCoordinatesY,
        fromDate: targetDate,
        toDate: targetDate,
    };
}

export async function syncBoundaryCustomStayDates(tourId: number, detail: TourDetail): Promise<TourDetail> {
    const stays = detail.stays ?? [];
    const hostStays = stays.filter((stay) => stay.stayType === HOST_STAY_TYPE);

    if (hostStays.length === 0) {
        return detail;
    }

    const earliestHostDate = hostStays.reduce<number | undefined>((earliest, stay) => {
        if (stay.fromDate == null) {
            return earliest;
        }

        return earliest == null ? stay.fromDate : Math.min(earliest, stay.fromDate);
    }, undefined);

    const latestHostDate = hostStays.reduce<number | undefined>((latest, stay) => {
        if (stay.toDate == null) {
            return latest;
        }

        return latest == null ? stay.toDate : Math.max(latest, stay.toDate);
    }, undefined);

    if (earliestHostDate == null || latestHostDate == null) {
        return detail;
    }

    let syncedDetail = detail;

    for (const [stayType, targetDate] of [
        [CUSTOM_START_STAY_TYPE, earliestHostDate],
        [CUSTOM_END_STAY_TYPE, latestHostDate],
    ] as const) {
        const boundaryStay = syncedDetail.stays?.find((stay) => stay.stayType === stayType);

        if (!boundaryStay?.stayId) {
            continue;
        }

        if (boundaryStay.fromDate === targetDate && boundaryStay.toDate === targetDate) {
            continue;
        }

        const payload = buildBoundaryCustomStayPayload(boundaryStay, targetDate);
        if (!payload) {
            continue;
        }

        syncedDetail = await updateCustomStay(tourId, boundaryStay.stayId, payload);
    }

    return syncedDetail;
}

export const deleteCustomStay = async (tourId: number, stayId: number): Promise<TourDetail> => {
    const response = await fetch(`${OpenAPI.BASE}/tours/${tourId}/mutations/custom-stays/${stayId}/delete`, {
        method: "PATCH",
        headers: getAuthorizedHeaders(),
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || "Unable to delete custom stay");
    }

    return response.json();
};


export const getTourDetail = (tourId: number, accessKey?: string)=>{
    return TourService.getTour(tourId, undefined, accessKey);
}

export const getTourDetailWithAlternativeRadius = async (
    tourId: number,
    alternativeRadiusKm: number,
    accessKey?: string,
): Promise<TourDetail> => {
    const params = new URLSearchParams();
    params.set("alternativeRadiusKm", String(alternativeRadiusKm));

    if (accessKey) {
        params.set("accessKey", accessKey);
    }

    const headers: Record<string, string> = {};
    const token = sessionStorage.getItem("token");
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${OpenAPI.BASE}/tours/${tourId}?${params.toString()}`, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        const responseMessage = await response.text();
        throw new Error(responseMessage || "Unable to load tour detail");
    }

    return response.json();
};

export const getStaysBetween = async (fromTimestamp: number, toTimestamp: number, hostId: number) => {
    return TourService.getStaysBetween(fromTimestamp, toTimestamp, hostId);
};

export const getVanliferNameByTourId = async (tourId: number) => {
    try {
        const tour = await TourService.getTour(tourId);
        const x = await UserVanliferService.getVanlifer(tour.vanliferId ?? -1);
        if (x.user && x.user.person) {
            return x.user.person?.firstname + " " + x.user.person?.lastname;
        }
        return "";
        } catch (error) {
        // Handle any errors here
        console.error(error);
        return "";
    }
};

export const cancelTour = (tourId: number) => {
    return TourService.cancelTour(tourId);
};

export const modifyTourCommunity = (
    tourId: number,
    name: string,
    description: string,
    sharingMode: "PRIVATE" | "PUBLIC",
    imageName?: string,
    fileExtension?: string,
) => {
    return TourService.updateTourCommunityAttributes(tourId, name, description, sharingMode, imageName, fileExtension);
};

export const toggleTourLike = (
    tourId: number,
    isLiked: boolean,
) => {
    return TourService.toggleTourLike(tourId, isLiked)
};

export const getPublicToursByUser = (communityName: string) => {
    return TourService.getPublicToursForVanlifer(communityName);
};

export const getPublicToursByUserLiked = (communityName: string) => {
    return TourService.getPublicToursByUserLiked(communityName);
};

export const getPublicMostLikedTours = () => {
    return TourService.getMostLikedTours();
};

export const getPublicOngoingTours = () => {
    return TourService.getOngoingTours();
};

export const getTourUploadLink = async (
    tourId: number,
    fileExtension: string
): Promise<SasUploadLinkResponse> => {
    return TourService.getSasUploadLink(tourId, fileExtension);
};
